<script setup lang="ts">
// Small "plan de ferme" map widget: shows the current site's champs (fields)
// centered on them, with a continuously looping radar (RainViewer) animation.
// Derived from the full map page (src/pages/carte.vue).
import { MapboxMap, MapboxLayer } from '@studiometa/vue-mapbox-gl'
import { notifyError } from '@/utils/helpers'
import 'mapbox-gl/dist/mapbox-gl.css'

const storeU = useUtilisateurStore()
const theme = useTheme()
const router = useRouter()
const isDark = computed(() => theme.global.current.value.dark)

function goToCarte() {
    router.push({ path: '/carte', query: { layer: 'champs' } })
}

// Public Mapbox token (same as the carte page)
const accessToken = 'pk.eyJ1IjoiYWdyaW1ldHJpZSIsImEiOiJjbTc3cjhrMTgxZGNqMnFxOGM4aGpjaW5hIn0.fUMzok-3aNnM9Zg7hpBKjQ'
const mapRef = ref<InstanceType<typeof MapboxMap> | null>(null)
const mapReady = ref(false)

const site = computed(() => storeU.siteSélectionné)
const hasSiteCoords = computed(
    () => Number.isFinite(site.value?.longitudeGoogleMaps) && Number.isFinite(site.value?.latitudeGoogleMaps),
)
const mapCenter = ref<[number, number]>([
    site.value?.longitudeGoogleMaps ?? -73.5,
    site.value?.latitudeGoogleMaps ?? 45.5,
])

// ── Champs (plan de ferme) ────────────────────────────────────────────────
const champsData = ref({ type: 'FeatureCollection', features: [] } as any)
const hasChamps = computed(() => champsData.value.features.length > 0)

const producerColors = [
    '#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336',
    '#00BCD4', '#E91E63', '#8BC34A', '#3F51B5', '#FF5722',
    '#009688', '#673AB7', '#CDDC39', '#795548', '#607D8B',
    '#FFC107', '#03A9F4', '#76FF03', '#FF4081', '#536DFE',
]

const producerColorMap = computed(() => {
    const uniqueProducers = [...new Set(
        champsData.value.features
            .map((f: any) => f.properties?.fast_carto)
            .filter((v: any) => v != null),
    )] as string[]
    const map = new Map<string, string>()
    uniqueProducers.forEach((producer, i) => {
        map.set(producer, producerColors[i % producerColors.length])
    })
    return map
})

function buildFillColorExpression(): any {
    const entries = [...producerColorMap.value.entries()]
    if (entries.length === 0) return '#FFEB3B'
    const expr: any[] = ['match', ['get', 'fast_carto']]
    for (const [key, color] of entries) expr.push(key, color)
    expr.push('#FFEB3B')
    return expr
}

const champsLayerOptions = computed(() => ({
    type: 'fill',
    source: { type: 'geojson', data: champsData.value },
    paint: {
        'fill-color': buildFillColorExpression(),
        'fill-opacity': 0.35,
        'fill-outline-color': buildFillColorExpression(),
    },
}))

const champsOutlineLayerOptions = computed(() => ({
    type: 'line',
    source: { type: 'geojson', data: champsData.value },
    paint: {
        'line-color': buildFillColorExpression(),
        'line-width': 2,
    },
}))

function pushChampsData() {
    const map = mapRef.value?.map
    if (!map) return
    if (map.getSource('widget-champs-fill')) {
        ;(map.getSource('widget-champs-fill') as any).setData(champsData.value)
    }
    if (map.getSource('widget-champs-line')) {
        ;(map.getSource('widget-champs-line') as any).setData(champsData.value)
    }
    if (map.getLayer('widget-champs-fill')) {
        map.setPaintProperty('widget-champs-fill', 'fill-color', buildFillColorExpression())
        map.setPaintProperty('widget-champs-fill', 'fill-outline-color', buildFillColorExpression())
    }
    if (map.getLayer('widget-champs-line')) {
        map.setPaintProperty('widget-champs-line', 'line-color', buildFillColorExpression())
    }
}

/** Fit the map view to all champs polygons, or fall back to the site centre. */
function fitToChamps() {
    const map = mapRef.value?.map
    if (!map) return

    const points: number[][] = []
    for (const feature of champsData.value.features) {
        const geom = feature.geometry
        if (!geom) continue
        if (geom.type === 'Polygon') {
            points.push(...geom.coordinates[0])
        } else if (geom.type === 'MultiPolygon') {
            for (const poly of geom.coordinates) points.push(...poly[0])
        }
    }

    if (points.length === 0) {
        if (hasSiteCoords.value) {
            map.flyTo({ center: mapCenter.value, zoom: 8, speed: 1.4 })
        }
        return
    }

    const lngs = points.map(p => p[0])
    const lats = points.map(p => p[1])
    map.fitBounds(
        [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        { padding: 90, maxZoom: 9, duration: 1200 },
    )
}

async function getChamps() {
    if (!storeU.siteAAccès('prisme.base')) return
    try {
        const siteCS = storeU.siteSélectionné.lettresDappel
        if (!siteCS) return
        const result = await apiProphet.get(`prsm/${siteCS}/champs/geojson`)
        const jsons = result.data as any[]

        const allFeatures: any[] = []
        for (const geojson of jsons) {
            if (geojson.type === 'FeatureCollection' && geojson.features) {
                allFeatures.push(...geojson.features)
            } else if (geojson.type === 'Feature') {
                allFeatures.push(geojson)
            }
        }

        champsData.value = { type: 'FeatureCollection', features: allFeatures }
        pushChampsData()
        await nextTick()
        fitToChamps()
    } catch (error) {
        const status = (error as any)?.response?.status
        if (status === 404) {
            champsData.value = { type: 'FeatureCollection', features: [] }
            return
        }
        notifyError(error)
    }
}

// ── Radar (RainViewer – free, no key required) ─────────────────────────────
interface RadarFrame { time: number; path: string }
const radarFrames = ref<RadarFrame[]>([])
const radarHost = ref('https://tilecache.rainviewer.com')
const radarOpacity = 0.65
const currentFrameIndex = ref(0)
let radarTimer: ReturnType<typeof setInterval> | null = null
let radarRefreshInterval: ReturnType<typeof setInterval> | null = null

const currentFrame = computed<RadarFrame | null>(() => radarFrames.value[currentFrameIndex.value] ?? null)
const hasRadar = computed(() => radarFrames.value.length > 0)
const currentFrameLabel = computed(() => {
    const f = currentFrame.value
    if (!f) return ''
    return new Date(f.time * 1000).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })
})
const currentFrameIsForecast = computed(() => {
    const f = currentFrame.value
    return f ? f.time * 1000 > Date.now() : false
})

async function fetchRadarFrames() {
    try {
        const res = await fetch('https://api.rainviewer.com/public/weather-maps.json')
        const data = await res.json()
        radarHost.value = data.host ?? 'https://tilecache.rainviewer.com'
        const past = (data.radar?.past ?? []) as RadarFrame[]
        const nowcast = (data.radar?.nowcast ?? []) as RadarFrame[]
        radarFrames.value = [...past, ...nowcast]
        if (currentFrameIndex.value >= radarFrames.value.length) currentFrameIndex.value = 0
    } catch (e) {
        console.warn('[PlanDeFermeWidget] Failed to fetch radar frames', e)
    }
}

// Crossfade between two raster slots so frame swaps are smooth.
let radarActiveSlot = 0
let radarCleanupTimeout: ReturnType<typeof setTimeout> | null = null
const RADAR_FADE_MS = 350

function radarSlotIds(slot: number) {
    const s = slot === 0 ? 'a' : 'b'
    return { layerId: `widget-radar-${s}`, sourceId: `widget-radar-src-${s}` }
}

function removeRadarSlot(map: any, slot: number) {
    const { layerId, sourceId } = radarSlotIds(slot)
    if (map.getLayer(layerId)) map.removeLayer(layerId)
    if (map.getSource(sourceId)) map.removeSource(sourceId)
}

function applyRadarLayer() {
    const map = mapRef.value?.map
    if (!map) return
    if (!map.isStyleLoaded()) { map.once('styledata', applyRadarLayer); return }

    if (!currentFrame.value) {
        for (const slot of [0, 1]) {
            const { layerId } = radarSlotIds(slot)
            if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'raster-opacity', 0)
        }
        if (radarCleanupTimeout !== null) clearTimeout(radarCleanupTimeout)
        radarCleanupTimeout = setTimeout(() => {
            removeRadarSlot(map, 0)
            removeRadarSlot(map, 1)
        }, RADAR_FADE_MS + 50)
        return
    }

    if (radarCleanupTimeout !== null) { clearTimeout(radarCleanupTimeout); radarCleanupTimeout = null }

    const nextSlot = 1 - radarActiveSlot
    const { layerId: nextLayerId, sourceId: nextSourceId } = radarSlotIds(nextSlot)
    const { layerId: prevLayerId } = radarSlotIds(radarActiveSlot)

    const tileUrl = `${radarHost.value}${currentFrame.value.path}/512/{z}/{x}/{y}/6/1_1.png`
    // Insert below the field polygons so semi-transparent fields stay on top.
    const beforeLayer = map.getLayer('widget-champs-fill') ? 'widget-champs-fill' : undefined

    removeRadarSlot(map, nextSlot)
    map.addSource(nextSourceId, {
        type: 'raster',
        tiles: [tileUrl],
        tileSize: 512,
        maxzoom: 7,
        attribution: '© <a href="https://www.rainviewer.com" target="_blank">RainViewer</a>',
    })
    map.addLayer({
        id: nextLayerId,
        type: 'raster',
        source: nextSourceId,
        paint: {
            'raster-opacity': 0,
            'raster-opacity-transition': { duration: RADAR_FADE_MS, delay: 0 },
            'raster-fade-duration': 0,
        },
    }, beforeLayer)

    requestAnimationFrame(() => {
        if (map.getLayer(nextLayerId)) map.setPaintProperty(nextLayerId, 'raster-opacity', radarOpacity)
        if (map.getLayer(prevLayerId)) map.setPaintProperty(prevLayerId, 'raster-opacity', 0)
    })

    radarActiveSlot = nextSlot
    const outgoing = 1 - nextSlot
    radarCleanupTimeout = setTimeout(() => {
        removeRadarSlot(map, outgoing)
        radarCleanupTimeout = null
    }, RADAR_FADE_MS + 50)
}

function startRadarAnimation() {
    stopRadarAnimation()
    if (radarFrames.value.length === 0) return
    radarTimer = setInterval(() => {
        const isLast = currentFrameIndex.value === radarFrames.value.length - 1
        // Hold the most recent frame a little longer before looping back.
        currentFrameIndex.value = isLast ? 0 : currentFrameIndex.value + 1
    }, 600)
}

function stopRadarAnimation() {
    if (radarTimer !== null) { clearInterval(radarTimer); radarTimer = null }
}

watch(currentFrame, applyRadarLayer)

function onMapLoad() {
    mapReady.value = true
    pushChampsData()
    fitToChamps()
    applyRadarLayer()
}

// Refetch & recenter when the selected site changes.
watch(() => storeU.siteSélectionné, async () => {
    if (hasSiteCoords.value) {
        mapCenter.value = [site.value.longitudeGoogleMaps, site.value.latitudeGoogleMaps]
    }
    await getChamps()
})

onMounted(async () => {
    await getChamps()
    await fetchRadarFrames()
    startRadarAnimation()
    radarRefreshInterval = setInterval(fetchRadarFrames, 5 * 60 * 1000)
})

onUnmounted(() => {
    stopRadarAnimation()
    if (radarRefreshInterval !== null) clearInterval(radarRefreshInterval)
    if (radarCleanupTimeout !== null) clearTimeout(radarCleanupTimeout)
})
</script>

<template>
    <v-card class="plan-ferme-widget" rounded="xl" elevation="4">
        <div class="plan-ferme-widget__map">
            <MapboxMap
                ref="mapRef"
                style="width: 100%; height: 100%;"
                :access-token="accessToken"
                map-style="mapbox://styles/mapbox/satellite-streets-v12"
                :center="mapCenter"
                :zoom="11"
                @mb-load="onMapLoad"
            >
                <MapboxLayer id="widget-champs-fill" :options="champsLayerOptions" />
                <MapboxLayer id="widget-champs-line" :options="champsOutlineLayerOptions" />
            </MapboxMap>

            <!-- Radar time chip -->
            <v-chip
                v-if="hasRadar"
                size="small"
                variant="flat"
                :color="currentFrameIsForecast ? 'deep-purple' : 'primary'"
                class="plan-ferme-widget__time font-weight-medium"
                prepend-icon="M19.07,4.93L17.66,6.34C19.1,7.79 20,9.79 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12C4,7.92 7.05,4.56 11,4.07V6.09C8.16,6.57 6,9.03 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12C18,10.34 17.33,8.84 16.24,7.76L14.83,9.17C15.55,9.9 16,10.9 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12C8,10.14 9.28,8.59 11,8.14V10.28C10.4,10.63 10,11.26 10,12A2,2 0 0,0 12,14A2,2 0 0,0 14,12C14,11.26 13.6,10.62 13,10.28V2H12A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12C22,9.24 20.88,6.74 19.07,4.93Z"
            >
                {{ currentFrameLabel }}
                <span v-if="currentFrameIsForecast" class="ms-1 text-caption">(prév.)</span>
            </v-chip>

            <!-- Open full map -->
            <v-btn
                class="plan-ferme-widget__open"
                color="primary"
                size="small"
                variant="flat"
                rounded="pill"
                prepend-icon="M15,19L9,16.89V5L15,7.11M20.5,3C20.44,3 20.39,3 20.34,3L15,5.1L9,3L3.36,4.9C3.15,4.97 3,5.15 3,5.38V20.5A0.5,0.5 0 0,0 3.5,21C3.55,21 3.61,21 3.66,20.97L9,18.9L15,21L20.64,19.1C20.85,19 21,18.85 21,18.62V3.5A0.5,0.5 0 0,0 20.5,3Z"
                @click="goToCarte"
            >
                Carte
            </v-btn>

            <div v-if="mapReady && !hasChamps" class="plan-ferme-widget__empty">
                <v-icon icon="M23,4.1V2.3L21.2,2.1C21.1,2.1 20.5,2 19.5,2C15.4,2 12.4,3.2 10.7,5.3C9.4,4.5 7.6,4 5.5,4C4.5,4 3.8,4.1 3.8,4.1L1.9,4.4L2,6.1C2.1,9.1 3.6,14.8 8.8,14.8C8.9,14.8 8.9,14.8 9,14.8V18.2C5.2,18.7 2,20 2,20V22H22V20C22,20 18.8,18.7 15,18.2V15C21.3,14.9 23,7.8 23,4.1M12,18C11.7,18 11.3,18 11,18V12.4C11,12.4 10.8,9 8,9C8,9 9.5,9.8 9.9,12.7C9.5,12.8 9.1,12.8 8.8,12.8C4.2,12.8 4,6.1 4,6.1C4,6.1 4.6,6 5.5,6C7.4,6 10.5,6.4 11.4,9.1C11.9,4.6 17,4 19.5,4C20.4,4 21,4.1 21,4.1C21,4.1 21,13.1 14.7,13.1C14.5,13.1 14.2,13.1 14,13.1C14,11.1 16,8.1 16,8.1C13,9.1 13,13 13,13V18C12.7,18 12.3,18 12,18Z" size="20" class="me-1" />
                Aucun champ pour ce site
            </div>
        </div>
    </v-card>
</template>

<style scoped>
.plan-ferme-widget {
    overflow: hidden;
    background: v-bind('isDark ? "rgba(18, 20, 30, 0.88)" : "rgba(255, 255, 255, 0.92)"');
    backdrop-filter: blur(14px);
}

.plan-ferme-widget__map {
    position: relative;
    width: 100%;
    height: 300px;
}

.plan-ferme-widget__time {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 2;
}

.plan-ferme-widget__open {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 2;
}

/* Hide Mapbox attribution + logo controls (bottom marks) */
.plan-ferme-widget__map :deep(.mapboxgl-ctrl-bottom-left),
.plan-ferme-widget__map :deep(.mapboxgl-ctrl-bottom-right),
.plan-ferme-widget__map :deep(.mapboxgl-ctrl-logo),
.plan-ferme-widget__map :deep(.mapboxgl-ctrl-attrib) {
    display: none !important;
}

.plan-ferme-widget__empty {
    position: absolute;
    bottom: 10px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #fff;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    pointer-events: none;
}
</style>
